// 个人资料编辑页(/profile/edit)— frame pSKAo @ ui/dogdisk-user.pen
//
// 2026-06-11 改版:用户名仅允许修改一次(保留/修改三态)
//   username_confirmed_at == null → 显示「保留」+「修改」按钮组
//   username_confirmed_at != null → 只读,hint 已确认不可再改
//   点「修改」→ 进入 usernameEditing 状态,输入后 pSKAo-save 提交
//   点「保留」→ keepMutation.mutate(),成功后 profile 缓存自动刷新
//
// 其余字段:昵称/签名/手机号 可编辑,邮箱只读+badge,头像上传。
// 底部单一「保存修改」pSKAo-save 聚合写。
//
// 后端:username→update_username RPC、keep→username/keep、
//       phone→update_phone RPC、avatar→me/avatar;
//       nickname/signature 经 supabase-js PATCH profiles。
import { useRef, useState, type ChangeEvent, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ApiClientError,
  Spinner,
  getSupabase,
  useMyProfile,
  useSetAvatar,
  useUpdatePhone,
  useUpdateUsername,
  useKeepUsername,
} from '@dogdisk/shared'
import { AppLayout } from '../../components/app-layout'
import { MobileScreen, TopBar } from '../../components/mobile-screen'
import { uploadFile } from '../../_lib/upload'
import { clampSignature, signatureLength, SIGNATURE_MAX_LEN } from '../../_lib/signature'
import { resourceUrl } from '../../_lib/resource-node-pref'

const F = 'pSKAo'

const ERROR_MESSAGES: Record<string, string> = {
  USERNAME_CHANGE_LOCKED: '用户名已锁定，仅允许修改一次',
  USERNAME_TAKEN_IN_AGENT: '该用户号已被占用',
  INVALID_USERNAME: '用户号格式不合法（3-30 位字母/数字/下划线）',
  INVALID_PHONE: '手机号格式不正确（需 E.164 格式，例：+8613800138000）',
  PHONE_TAKEN_IN_AGENT: '该手机号已被绑定',
}

function describeError(err: unknown): string {
  if (err instanceof ApiClientError) return ERROR_MESSAGES[err.code] ?? err.message ?? err.code
  if (err instanceof Error) return err.message
  return String(err)
}

const PHONE_RE = /^\+[1-9]\d{6,14}$/

export function ProfileEditPage() {
  const navigate = useNavigate()
  const profileQuery = useMyProfile()
  const updateUsername = useUpdateUsername()
  const keepMutation = useKeepUsername()
  const updatePhone = useUpdatePhone()
  const setAvatarMutation = useSetAvatar()

  const profile = profileQuery.data
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [avatarUploading, setAvatarUploading] = useState(false)
  const [avatarError, setAvatarError] = useState<string | null>(null)

  // 表单 state(profile 到位后初始化)
  const [username, setUsername] = useState('')
  const [nickname, setNickname] = useState('')
  const [phone, setPhone] = useState('')
  const [signature, setSignature] = useState('')
  const [initialized, setInitialized] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saveOk, setSaveOk] = useState(false)

  // 用户名三态:confirmed(只读) / !confirmed+!editing(按钮组) / !confirmed+editing(可输入)
  const [usernameEditing, setUsernameEditing] = useState(false)

  if (profile && !initialized) {
    setUsername(profile.username)
    setNickname(profile.nickname ?? '')
    setPhone(profile.phone_real ?? '')
    setSignature(profile.signature ?? '')
    setInitialized(true)
  }

  if (profileQuery.isLoading || !profile) {
    return (
      <AppLayout>
        <MobileScreen>
          <TopBar title="个人资料" />
          <div className="flex flex-1 items-center justify-center py-12">
            <Spinner className="text-gray-400" />
          </div>
        </MobileScreen>
      </AppLayout>
    )
  }

  if (profileQuery.isError) {
    return (
      <AppLayout>
        <MobileScreen>
          <TopBar title="个人资料" />
          <div className="p-6 text-sm text-red-600">加载失败：{describeError(profileQuery.error)}</div>
        </MobileScreen>
      </AppLayout>
    )
  }

  const confirmed = profile.username_confirmed_at !== null

  const onPickAvatar = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setAvatarError(null)
    if (!/^image\//.test(file.type)) {
      setAvatarError('请选择图片文件（jpg/png/webp）')
      return
    }
    const MAX_AVATAR_BYTES = 5 * 1024 * 1024
    if (file.size > MAX_AVATAR_BYTES) {
      setAvatarError(`图片不能超过 5MB（当前 ${(file.size / 1024 / 1024).toFixed(1)}MB）`)
      return
    }
    setAvatarUploading(true)
    void uploadFile(file, {
      onSuccess: (resp) => {
        setAvatarMutation.mutate(resp.file_info_id, {
          onError: (err) => { setAvatarError(describeError(err)); setAvatarUploading(false) },
          onSuccess: () => setAvatarUploading(false),
        })
      },
      onError: (err) => { setAvatarError(describeError(err)); setAvatarUploading(false) },
    })
  }

  const onKeep = () => {
    setSaveError(null)
    setSaveOk(false)
    keepMutation.mutate(undefined, {
      onSuccess: () => {
        setUsernameEditing(false)
        setSaveOk(true)
      },
      onError: (err) => {
        // keep 幂等,失败仅 NotFound/网络错;无专属错误码,裸 message 兜底
        setSaveError(describeError(err))
      },
    })
  }

  // 单一「保存修改」:聚合 username / phone / nickname+signature 三路写;逐项收集错误。
  const onSave = async () => {
    setSaving(true)
    setSaveError(null)
    setSaveOk(false)
    const errs: string[] = []

    const nextUsername = username.trim()
    // 仅在 usernameEditing 且值有变化时调 updateUsername
    if (!confirmed && usernameEditing && nextUsername !== profile.username) {
      if (nextUsername.length < 3 || nextUsername.length > 30) {
        errs.push('用户号需 3-30 位')
      } else {
        try {
          await updateUsername.mutateAsync(nextUsername)
          setUsernameEditing(false)
        } catch (e) { errs.push(describeError(e)) }
      }
    }

    const nextPhone = phone.trim()
    if (nextPhone !== (profile.phone_real ?? '')) {
      if (nextPhone && !PHONE_RE.test(nextPhone)) {
        errs.push('手机号格式不正确（E.164，如 +8613800138000）')
      } else if (nextPhone) {
        try { await updatePhone.mutateAsync(nextPhone) } catch (e) { errs.push(describeError(e)) }
      }
    }

    const nextNick = nickname.trim()
    const nextSig = signature.trim()
    if (!nextNick) {
      errs.push('昵称不能为空')
    } else if (nextNick !== (profile.nickname ?? '') || nextSig !== (profile.signature ?? '')) {
      try {
        const sb = getSupabase()
        const { error } = await sb
          .from('profiles')
          .update({ nickname: nextNick || null, signature: nextSig || null })
          .eq('user_id', profile.user_id)
        if (error) throw error
      } catch (e) {
        errs.push(describeError(e))
      }
    }

    try {
      await profileQuery.refetch()
    } finally {
      setInitialized(false) // 用刷新后的 profile 重置表单基线
      setUsernameEditing(false) // 无论成功/失败均退出编辑态
      setSaving(false)
    }
    if (errs.length) setSaveError(errs.join('；'))
    else setSaveOk(true)
  }

  return (
    <AppLayout>
      <MobileScreen>
        <TopBar title="个人资料" />

        <main data-testid={F} className="flex flex-1 flex-col gap-5 px-6 pt-4 pb-32">
          {/* 头像 + 更换头像 pill */}
          <section className="flex flex-col items-center gap-3 pt-2">
            <div className="relative">
              <span className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-full bg-[#F1F2F4]">
                {profile.avatar_url ? (
                  <img src={resourceUrl(profile.avatar_url)} alt={profile.username} className="h-full w-full object-cover" />
                ) : (
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2"><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 4-7 8-7s8 3 8 7" /></svg>
                )}
                {(avatarUploading || setAvatarMutation.isPending) && (
                  <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40 text-white"><Spinner className="text-white" /></div>
                )}
              </span>
              <button
                type="button"
                data-testid={`${F}-avatar-btn`}
                aria-label="更换头像"
                onClick={() => fileInputRef.current?.click()}
                disabled={avatarUploading || setAvatarMutation.isPending}
                className="absolute -bottom-1 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-[#19caad] px-3 py-1 text-[11px] font-semibold text-white shadow active:bg-[#14a18b] disabled:opacity-50"
              >
                更换头像
              </button>
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={onPickAvatar} />
            {avatarError && <p role="alert" className="text-[12px] text-red-600">{avatarError}</p>}
          </section>

          {/* 用户号 — 三态 */}
          <Field label="用户号">
            {confirmed ? (
              /* 态 1: 已确认, 只读 */
              <>
                <div
                  data-testid={`${F}-username`}
                  className="flex h-12 w-full items-center rounded-xl bg-[#F6F7F8] px-4 text-[14px] text-[#9CA3AF]"
                >
                  {profile.username}
                </div>
                <p className="text-xs text-[#9CA3AF]">用户名已确认，不可再修改</p>
              </>
            ) : usernameEditing ? (
              /* 态 3: 编辑中 */
              <input
                data-testid={`${F}-username`}
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                minLength={3}
                maxLength={30}
                autoFocus
                className="h-12 w-full rounded-xl bg-[#F6F7F8] px-4 text-[14px] text-[#1A1A1A] outline-none focus:ring-1 focus:ring-[#19caad]"
              />
            ) : (
              /* 态 2: 未确认, 按钮组 */
              <>
                <div className="flex items-center gap-2">
                  <div
                    data-testid={`${F}-username`}
                    className="flex h-12 flex-1 items-center rounded-xl bg-[#F6F7F8] px-4 text-[14px] text-[#1A1A1A]"
                  >
                    {profile.username}
                  </div>
                  <button
                    type="button"
                    data-testid={`${F}-username-keep`}
                    onClick={onKeep}
                    disabled={keepMutation.isPending || saving}
                    className="h-8 shrink-0 rounded-lg bg-[#19caad] px-3.5 text-sm font-medium text-white disabled:opacity-50"
                  >
                    保留
                  </button>
                  <button
                    type="button"
                    data-testid={`${F}-username-edit`}
                    onClick={() => setUsernameEditing(true)}
                    disabled={keepMutation.isPending || saving}
                    className="h-8 shrink-0 rounded-lg border border-[#D1D5DB] bg-white px-3.5 text-sm text-[#1A1A1A] disabled:opacity-50"
                  >
                    修改
                  </button>
                </div>
                <p className="text-xs text-[#9CA3AF]">用户名仅允许修改一次，选择保留或修改</p>
              </>
            )}
          </Field>

          {/* 昵称 */}
          <Field label="昵称">
            <input
              data-testid={`${F}-nickname`}
              type="text"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              maxLength={30}
              placeholder="给自己起个昵称"
              className="h-12 w-full rounded-xl bg-[#F6F7F8] px-4 text-[14px] text-[#1A1A1A] outline-none focus:ring-1 focus:ring-[#19caad]"
            />
          </Field>

          {/* 邮箱 — gLS2M: badge + 修改(按绑定态分支跳转) */}
          <Field label="邮箱">
            <div className="flex items-center justify-end">
              <button
                type="button"
                data-testid={`${F}-email-edit`}
                onClick={() =>
                  navigate(profile.email_verified_at ? '/profile/change-email' : '/profile/email-bind')
                }
                className="rounded-lg bg-[#19caad] px-3 py-1 text-xs font-medium text-white active:bg-[#14a18b]"
              >
                修改
              </button>
            </div>
            <div data-testid={`${F}-email`} className="flex h-12 items-center justify-between rounded-xl bg-[#F6F7F8] px-4">
              <span className="truncate text-[14px] text-[#4B5563]">{profile.email_real ?? '未绑定'}</span>
              {profile.email_verified_at ? (
                <span className="ml-2 shrink-0 rounded bg-[#DCFCE7] px-2 py-0.5 text-[11px] text-[#16A34A]">已验证</span>
              ) : (
                <span className="ml-2 shrink-0 rounded bg-[#FFF1DB] px-2 py-0.5 text-[11px] text-[#D97706]">未验证</span>
              )}
            </div>
          </Field>

          {/* 手机号(可编辑 + 已绑定 badge) */}
          <Field label="手机号">
            <div className="flex items-center gap-2">
              <input
                data-testid={`${F}-phone`}
                type="tel"
                inputMode="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+8613800138000"
                className="h-12 flex-1 rounded-xl bg-[#F6F7F8] px-4 text-[14px] text-[#1A1A1A] outline-none focus:ring-1 focus:ring-[#19caad]"
              />
              {profile.phone_verified_at && (
                <span className="shrink-0 rounded bg-[#FFF1DB] px-2 py-1 text-[11px] text-[#D97706]">已绑定</span>
              )}
            </div>
          </Field>

          {/* 个性签名(≤50 字,码点截断,与 DB CHECK 一致) */}
          <Field label="个性签名">
            <textarea
              data-testid={`${F}-signature`}
              value={signature}
              onChange={(e) => setSignature(clampSignature(e.target.value))}
              rows={3}
              placeholder="这个人很懒，什么都没写…"
              className="h-20 w-full resize-none rounded-xl bg-[#F6F7F8] px-4 py-3 text-[14px] text-[#1A1A1A] outline-none focus:ring-1 focus:ring-[#19caad]"
            />
            <p
              data-testid={`${F}-signature-count`}
              className="mt-1 text-right text-[12px] text-[#9CA3AF]"
            >
              {signatureLength(signature)}/{SIGNATURE_MAX_LEN}
            </p>
          </Field>

          {saveError && <p role="alert" className="text-[12px] text-red-600">{saveError}</p>}
          {saveOk && <p role="status" className="text-[12px] text-[#19B665]">已保存</p>}
        </main>

        {/* 底部单一「保存修改」 */}
        <div
          className="fixed inset-x-0 bottom-0 z-20 mx-auto max-w-[402px] bg-white px-6 pt-3"
          style={{ paddingBottom: 'max(34px, env(safe-area-inset-bottom))' }}
        >
          <button
            type="button"
            data-testid={`${F}-save`}
            onClick={() => void onSave()}
            disabled={saving}
            className="h-12 w-full rounded-xl bg-[#19caad] text-[15px] font-semibold text-white active:bg-[#14a18b] disabled:opacity-50"
          >
            {saving ? '保存中…' : '保存修改'}
          </button>
        </div>
      </MobileScreen>
    </AppLayout>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-base font-semibold text-[#1A1A1A]">{label}</label>
      {children}
    </div>
  )
}

export default ProfileEditPage
